import { useApp } from '@/contexts';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Step,
  StepButton,
  Stepper,
  Typography,
} from '@mui/material';

import type { StepConfig, WizardConfig, WizardStepData } from '../types/wizard';
import { WizardCronStep } from './steps/WizardCronStep';
import { WizardDeployStep } from './steps/WizardDeployStep';
import { WizardEnvironmentStep } from './steps/WizardEnvironmentStep';
import { WizardMappingStep } from './steps/WizardMappingStep';
import { WizardNameStep } from './steps/WizardNameStep';
import { WizardPreviewStep } from './steps/WizardPreviewStep';
import { WizardSFTPFileProcessingStep } from './steps/WizardSFTPFileProcessingStep';
import { WizardScenarioStep } from './steps/WizardScenarioStep';
import { WizardSchemaStep } from './steps/WizardSchemaStep';

interface WizardFormStepperProps {
  config: WizardConfig;
  steps?: StepConfig[];
  activeStep: number;
  highestStep: number;
  showApiNames: boolean;
  wizardData: WizardStepData;
  onUpdateData: (data: Partial<WizardStepData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  onGoToStep: (step: number) => void;
  onToggleApiNames: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onSaveAndExit: () => void;
  onExitWithoutSaving: () => void;
}

export const WizardFormStepper = ({
  config,
  steps: externalSteps,
  activeStep,
  highestStep,
  showApiNames,
  wizardData,
  onUpdateData,
  onNext,
  onPrevious,
  onGoToStep,
  onToggleApiNames,
  onComplete,
  onCancel,
  onSaveAndExit,
  onExitWithoutSaving,
}: WizardFormStepperProps) => {
  const { appId } = useApp();

  // Use steps from props if provided, otherwise from config
  const steps: StepConfig[] = externalSteps || config.steps || [];

  const handleStepData = (stepData: Partial<WizardStepData>) => {
    onUpdateData(stepData);
    onNext();
  };

  const renderStepContent = (step: StepConfig) => {
    const { key, type, label } = step;

    switch (type) {
      case 'name':
        return (
          <WizardNameStep
            onNext={(name) => handleStepData({ name })}
            initialValue={wizardData.name}
          />
        );

      case 'env':
        return (
          <WizardEnvironmentStep
            sourceType={config.source?.type || ''}
            destinationType={config.destination?.type || ''}
            onNext={(sourceEnv, destinationEnv, sourceConnection, destinationConnection) =>
              handleStepData({ sourceEnv, destinationEnv, sourceConnection, destinationConnection })
            }
            initialSource={wizardData.sourceEnv}
            initialDestination={wizardData.destinationEnv}
            initialSourceConnection={wizardData.sourceConnection}
            initialDestinationConnection={wizardData.destinationConnection}
          />
        );

      case 'schema':
        return (
          <WizardSchemaStep
            connectionKey={wizardData.sourceConnection || config.source?.connectionKey || ''}
            providerType={config.source?.type || ''}
            showApiNames={showApiNames}
            onNext={(selectedObjects) => handleStepData({ selectedObjects })}
            onSaveAndExit={onSaveAndExit}
            initialSelected={wizardData.selectedObjects}
          />
        );

      case 'map':
        return (
          <WizardMappingStep
            sourceObject={wizardData.selectedObjects?.[0] || 'Source'}
            destinationObject="Destination"
            sourceFields={[]}
            destinationFields={[]}
            showApiNames={showApiNames}
            onNext={(mappings) => handleStepData({ mappings })}
            onSaveAndExit={onSaveAndExit}
            initialMappings={wizardData.mappings}
          />
        );

      case 'scenario':
        return (
          <WizardScenarioStep
            typeGuid={config.typeGuid}
            onNext={(scenarioId) => handleStepData({ scenarioId })}
            onSaveAndExit={onSaveAndExit}
            initialScenarioId={wizardData.scenarioId}
          />
        );

      case 'cron':
        return (
          <WizardCronStep
            onNext={(schedule) => handleStepData({ schedule })}
            onSaveAndExit={onSaveAndExit}
            initialSchedule={wizardData.schedule}
          />
        );

      case 'sftpFileProcessing':
        return (
          <WizardSFTPFileProcessingStep
            onNext={(fileProcessing) => handleStepData({ fileProcessing })}
            onSaveAndExit={onSaveAndExit}
            initialFileProcessing={wizardData.fileProcessing}
          />
        );

      case 'preview':
        return <WizardPreviewStep data={wizardData} onNext={onNext} onBack={onPrevious} />;

      case 'deploy':
        return (
          <WizardDeployStep
            onComplete={onComplete}
            onBack={onPrevious}
            onSaveAndExit={onSaveAndExit}
          />
        );

      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography>Step type "{type}" not implemented yet.</Typography>
            <Button onClick={onNext} sx={{ mt: 2 }}>
              Skip
            </Button>
          </Box>
        );
    }
  };

  const currentStep = steps[activeStep];
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  // Navigation buttons layout matching navButtons.js
  const renderNavigationButtons = () => {
    // Deploy step has its own buttons
    if (currentStep.type === 'deploy') {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
        {/* Left side: Back or Cancel */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isFirstStep ? (
            <Button variant="outlined" color="error" onClick={onCancel}>
              Cancel
            </Button>
          ) : (
            <Button variant="outlined" onClick={onPrevious}>
              Back
            </Button>
          )}
        </Box>

        {/* Right side: Next, Save and Exit, Exit Without Saving */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Exit Without Saving - only show if draft exists */}
          {wizardData.name && (
            <Button variant="outlined" color="warning" onClick={onExitWithoutSaving}>
              Exit Without Saving
            </Button>
          )}

          {/* Save and Exit - not on deploy step */}
          {!isLastStep && (
            <Button variant="outlined" onClick={onSaveAndExit}>
              Save and Exit
            </Button>
          )}

          {/* Next or Finish */}
          {isLastStep ? (
            <Button variant="contained" onClick={onComplete}>
              Finish
            </Button>
          ) : (
            <Button variant="contained" onClick={onNext}>
              Next
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Stepper with clickable steps (up to highestStep) */}
      <Stepper activeStep={activeStep} nonLinear sx={{ mb: 4 }}>
        {steps.map((step, index) => (
          <Step key={step.key} completed={index < activeStep}>
            <StepButton onClick={() => onGoToStep(index)} disabled={index > highestStep}>
              {step.label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      {/* API Names Toggle (for schema and map steps) */}
      {(currentStep.type === 'schema' || currentStep.type === 'map') && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={showApiNames} onChange={onToggleApiNames} />}
            label="Show API Names"
          />
        </Box>
      )}

      {/* Step Content */}
      <Box sx={{ minHeight: 400 }}>{currentStep ? renderStepContent(currentStep) : null}</Box>

      {/* Navigation Buttons */}
      {renderNavigationButtons()}
    </Box>
  );
};
